import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { faroTableComparator } from "@components/common/faro-table/faro-table-utils";
import { RoleColumn } from "@components/table/members/members-column/role-column/role-column";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { TeamName } from "@components/table/teams/team-name";
import { TEAMS_TABLE_COLUMN_LABELS } from "@components/table/teams/teams-table-constants";
import {
  TeamsTableColumnField,
  TeamsTableGridColDef,
} from "@components/table/teams/teams-table-types";
import { TeamsMembersColumn } from "@components/table/teams/teams-members-column";
import { mapTeamMembersToSdbMembers } from "@components/table/teams/teams-table-utils";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbTeam } from "@custom-types/teams-types";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { useDateTime } from "@hooks/use-date-time";
import {
  GridRenderCellParams,
  gridStringOrNumberComparator,
} from "@mui/x-data-grid-pro";
import { formatUserRoleType } from "@utils/data-display";
import { isTeamsTableColumnField } from "@custom-types/type-guards";
import { TeamOptionsMenu } from "@components/table/teams/team-options-menu";

interface Props extends BaseCompanyIdProps {
  /** The map of company members by Id */
  companyMembersMap: Map<string, SdbMemberTypes>;
}

/** Return all the possible columns for Teams table */
export function useTeamsTableColumns({
  companyId,
  companyMembersMap,
}: Props): TeamsTableGridColDef[] {
  const { formatDate } = useDateTime();

  const { columnPreferences, visibleOrderedColumns } =
    useColumnPreferencesContext<TeamsTableColumnField>();

  const columnsMap: Record<TeamsTableColumnField, TeamsTableGridColDef> = {
    name: {
      field: "name",
      headerName: TEAMS_TABLE_COLUMN_LABELS.name,
      flex: 1,
      minWidth: 220,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      disableReorder: true,
      renderCell: (params: GridRenderCellParams<{ entity: SdbTeam }>) => {
        return <TeamName team={params.row.entity} />;
      },
      valueGetter: (_, row: { entity: SdbTeam }) => row.entity.name,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    users: {
      field: "users",
      minWidth: 220,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon title={TEAMS_TABLE_COLUMN_LABELS.users} />
      ),
      renderCell: (params: GridRenderCellParams<{ entity: SdbTeam }>) => {
        return (
          <TeamsMembersColumn
            members={mapTeamMembersToSdbMembers(
              params.row.entity.sampleMembers,
              params.row.entity.role,
              companyMembersMap
            )}
            membersCount={params.row.entity.memberCount}
          />
        );
      },
      valueGetter: (_, row: { entity: SdbTeam }) => row.entity.memberCount,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    creationDate: {
      field: "creationDate",
      minWidth: 180,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={TEAMS_TABLE_COLUMN_LABELS.creationDate}
        />
      ),
      renderCell: (params: GridRenderCellParams<{ entity: SdbTeam }>) => {
        return (
          <var>
            {formatDate(params.row.entity.createdAt)}
          </var>
        );
      },
      valueGetter: (_, row: { entity: SdbTeam }) =>
        new Date(row.entity.createdAt).getTime(),
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    workspaceRole: {
      field: "workspaceRole",
      minWidth: 180,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={TEAMS_TABLE_COLUMN_LABELS.workspaceRole}
        />
      ),
      renderCell: (params: GridRenderCellParams<{ entity: SdbTeam }>) => (
        <RoleColumn
          companyId={companyId}
          subjectType="team"
          memberIdentityRole={{
            identity: params.row.entity.identity,
            role: params.row.entity.role,
          }}
        />
      ),
      valueGetter: (_, row: { entity: SdbTeam }) =>
        formatUserRoleType(row.entity.role),
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    options: {
      field: "options",
      type: "actions",
      headerName: "",
      width: 90,
      renderHeader: () => (
        <FaroTableColumnVisibilityMenuIcon
          columnPreferences={columnPreferences}
        />
      ),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      disableReorder: true,
      renderCell: (params: GridRenderCellParams<{ entity: SdbTeam }>) => {
        return <TeamOptionsMenu team={params.row.entity} />;
      },
    },
  };

  // Return only visible columns in the order specified by preferences
  return visibleOrderedColumns
    .filter(isTeamsTableColumnField)
    .map((field) => columnsMap[field]);
}
